import React, { useEffect, useState, useRef, useMemo } from "react";
import axiosWithAuth from "../../../utils/axiosWithAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Form,
  Input,
  InputNumber,
  Divider,
  Modal,
  Button,
  message,
  Drawer,
  Checkbox,
  Switch,
  Tooltip,
} from "antd";
import { faCartShopping, faUtensils } from "@fortawesome/free-solid-svg-icons";
import { DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import { FE_URL } from "../../../common/constants";
import RestaurantOffersCarousel from "./RestaurantOffersCarousel";
import formatBusinessName from "../../../utils/formatBusinessName";

import TipSelector from "../../../common/TipSelector";
import { calculateTip } from "../../../utils/checkoutTip";

const CART_STORAGE_TTL_MS = 10 * 60 * 1000;

const getNumberValue = (value) => Number(value) || 0;

const getDishPrice = (dish) => {
  if (!dish) {
    return 0;
  }

  return getNumberValue(
    dish.on_offer === 1 ? dish.dish_offer_price : dish.dish_price,
  );
};

const getDishExtras = (dish) =>
  Array.isArray(dish?.extras) ? dish.extras : [];

const getDiscountPercentage = (regularPrice, offerPrice) => {
  const regular = getNumberValue(regularPrice);
  const offer = getNumberValue(offerPrice);

  if (regular <= 0 || offer <= 0 || offer >= regular) {
    return 0;
  }

  return Math.round(((regular - offer) / regular) * 100);
};

export default function ProfileRestaurantsPage(props) {
  const { business } = props;
  const cartStorageKey =
    business && business.id ? `restaurant_cart_${business.id}` : null;
  const stripeStatus = business?.stripe_status || {};
  const canBuy =
    stripeStatus.isActive === true && stripeStatus.payoutsEnabled === true;

  const [menuItems, setMenuItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [SelectedMenuItem, setSelectedMenuItem] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [cart, setCart] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [total, setTotal] = useState(0);
  const [tipSelection, setTipSelection] = useState(0);
  const [customTip, setCustomTip] = useState(null);
  const tipAmount = calculateTip(total, tipSelection, customTip);

  useEffect(() => {
    setTipSelection(0);
    setCustomTip(null);
  }, [business.id]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failedDishImages, setFailedDishImages] = useState({});
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [checkoutDrawerOpen, setCheckoutDrawerOpen] = useState(false);
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  const [deliveryError, setDeliveryError] = useState(null);
  const [formValues, setFormValues] = useState({
    address: "",
    zip_code: "",
    phone_number: "",
    email: "",
  });

  //Radio Button Group
  const [enable, setEnable] = useState(1);

  // Refs for scroll behavior
  const businessNameRef = useRef(null);
  const categoryRefs = useRef({});

  const isSpecialsCategory = (category) =>
    String(category || "")
      .trim()
      .toLowerCase() === "specials";

  const getCategoryDisplayName = (category) =>
    isSpecialsCategory(category) ? "Daily Specials" : category;

  const getCartTotal = (cartItems) =>
    cartItems.reduce((sum, item) => sum + (item.itemTotal || 0), 0);

  const getCartItemCount = (cartItems) =>
    cartItems.reduce((sum, item) => sum + (item.itemQuantity || 1), 0);

  const markDishImageAsFailed = (imageKey) => {
    if (imageKey === undefined || imageKey === null) {
      return;
    }

    setFailedDishImages((currentFailedImages) =>
      currentFailedImages[imageKey]
        ? currentFailedImages
        : {
            ...currentFailedImages,
            [imageKey]: true,
          },
    );
  };

  const renderDishImage = (menuItem, imageClassName, iconSize) => {
    const imageKey =
      menuItem?.dish_id || menuItem?.itemID || menuItem?.dish_image;
    const hasWorkingImage =
      menuItem && menuItem.dish_image && !failedDishImages[imageKey];

    if (!hasWorkingImage) {
      return (
        <div className="no-image-placeholder">
          <FontAwesomeIcon
            icon={faUtensils}
            style={iconSize ? { fontSize: iconSize } : undefined}
          />
        </div>
      );
    }

    return (
      <img
        className={imageClassName}
        src={menuItem.dish_image}
        alt={menuItem.dish_name || menuItem.itemName || "Menu item"}
        onError={() => markDishImageAsFailed(imageKey)}
      />
    );
  };

  const saveRestaurantCart = (nextCart, nextTotal) => {
    if (!cartStorageKey || !business.id) {
      return;
    }

    if (!nextCart || nextCart.length === 0) {
      localStorage.removeItem(cartStorageKey);
      return;
    }

    localStorage.setItem(
      cartStorageKey,
      JSON.stringify({
        restaurantId: String(business.id),
        cart: nextCart,
        total: nextTotal,
        expiresAt: Date.now() + CART_STORAGE_TTL_MS,
      }),
    );
  };

  const clearSavedRestaurantCart = () => {
    if (cartStorageKey) {
      localStorage.removeItem(cartStorageKey);
    }
  };

  // Obtener categorías únicas
  const categories = useMemo(() => {
    return [
      ...new Set(
        menuItems
          .filter((item) => item.is_available === 1)
          .map((item) => item.category),
      ),
    ].sort((a, b) => {
      const aIsSpecials = isSpecialsCategory(a);
      const bIsSpecials = isSpecialsCategory(b);

      if (aIsSpecials && !bIsSpecials) {
        return -1;
      }

      if (!aIsSpecials && bIsSpecials) {
        return 1;
      }

      return 0;
    });
  }, [menuItems]);

  useEffect(() => {
    const APIEndPoint = `/api/restaurant/dish/${business.id}/categ`;
    axiosWithAuth()
      .get(APIEndPoint)
      .then((res) => {
        setMenuItems(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [business.id]);

  useEffect(() => {
    if (!cartStorageKey || !business.id) {
      return;
    }

    try {
      const savedRestaurantCart = localStorage.getItem(cartStorageKey);

      if (!savedRestaurantCart) {
        setCart([]);
        setTotal(0);
        return;
      }

      const parsedCart = JSON.parse(savedRestaurantCart);
      const isValidSavedCart =
        parsedCart &&
        String(parsedCart.restaurantId) === String(business.id) &&
        parsedCart.expiresAt > Date.now() &&
        Array.isArray(parsedCart.cart);

      if (!isValidSavedCart) {
        localStorage.removeItem(cartStorageKey);
        setCart([]);
        setTotal(0);
        return;
      }

      setCart(parsedCart.cart);
      setTotal(
        typeof parsedCart.total === "number"
          ? parsedCart.total
          : getCartTotal(parsedCart.cart),
      );
    } catch (error) {
      console.error("Error loading saved restaurant cart:", error);
      localStorage.removeItem(cartStorageKey);
      setCart([]);
      setTotal(0);
    }
  }, [business.id, cartStorageKey]);

  // Detectar scroll para mostrar/ocultar sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (businessNameRef.current) {
        const rect = businessNameRef.current.getBoundingClientRect();
        const shouldShow = rect.bottom < 0;
        setShowStickyHeader(shouldShow);

        // Actualizar clase en body para ajustes de padding si es necesario
        if (shouldShow) {
          document.body.classList.add("sticky-header-visible");
        } else {
          document.body.classList.remove("sticky-header-visible");
        }
      }

      // Detectar categoría activa basándose en scroll siguiendo el orden real renderizado
      let nextActiveCategory = null;

      for (let i = categories.length - 1; i >= 0; i--) {
        const category = categories[i];
        const element = categoryRefs.current[category];

        if (!element) {
          continue;
        }

        const rect = element.getBoundingClientRect();
        if (rect.top <= 220) {
          nextActiveCategory = category;
          break;
        }
      }

      if (!nextActiveCategory && categories.length > 0) {
        nextActiveCategory = categories[0];
      }

      if (nextActiveCategory) {
        setActiveCategory((currentCategory) =>
          currentCategory === nextActiveCategory
            ? currentCategory
            : nextActiveCategory,
        );
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Ejecutar una vez al montar

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.classList.remove("sticky-header-visible");
    };
  }, [categories]);

  // Cargar información del usuario desde localStorage al iniciar
  useEffect(() => {
    setUserInfo(null);
    setDeliveryPrice(0);
    setEnable(1);
    setDeliveryError(null);
    setFormValues({
      address: "",
      zip_code: "",
      phone_number: "",
      email: "",
    });

    const savedUserInfo = localStorage.getItem(
      `userInfo_restaurant_${business.id}`,
    );
    const savedDeliveryPrice = localStorage.getItem(
      `deliveryPrice_restaurant_${business.id}`,
    );
    const savedDeliveryType = localStorage.getItem(
      `deliveryType_restaurant_${business.id}`,
    );

    if (savedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(savedUserInfo);
        setUserInfo(parsedUserInfo);
        setFormValues(parsedUserInfo);
      } catch (e) {
        console.error("Error parsing saved user info:", e);
      }
    }

    if (savedDeliveryPrice) {
      try {
        setDeliveryPrice(JSON.parse(savedDeliveryPrice));
      } catch (e) {
        console.error("Error parsing saved delivery price:", e);
      }
    }

    if (savedDeliveryType) {
      try {
        setEnable(JSON.parse(savedDeliveryType));
      } catch (e) {
        console.error("Error parsing saved delivery type:", e);
      }
    }
  }, [business.id]);

  // Resetear delivery price y error cuando se cambia a Pickup
  useEffect(() => {
    if (enable === 0) {
      setDeliveryPrice(0);
      setDeliveryError(null);
    }
  }, [enable]);

  // Función para calcular delivery price manualmente
  const calculateDeliveryPrice = async () => {
    // Validar que tengamos address y zip_code
    if (!formValues.address || !formValues.zip_code) {
      message.error("Please enter your address and zip code first.");
      return;
    }

    if (formValues.address.length <= 5) {
      message.error("Please enter a valid address.");
      return;
    }

    if (formValues.zip_code.length < 5) {
      message.error("Please enter a valid zip code.");
      return;
    }

    setIsCalculatingDelivery(true);
    setDeliveryError(null);

    try {
      const response = await axiosWithAuth().post(
        `${process.env.REACT_APP_BE_API_URL}/api/restaurant/order/shippingPriceRestaurant/${business.id}`,
        {
          deliveryAddress: formValues.address,
          zipCode: formValues.zip_code,
          typeDelivery: "Delivery",
        },
      );

      const price = response.data.data.price;
      setDeliveryPrice(price);

      // Guardar en localStorage
      localStorage.setItem(
        `deliveryPrice_restaurant_${business.id}`,
        JSON.stringify(price),
      );

      message.success(`Delivery fee calculated: $${price.toFixed(2)}`);
    } catch (err) {
      console.log("Error calculating delivery price:", err);

      // Capturar el mensaje de error del servidor
      const errorMessage =
        err.response?.data?.message ||
        "Delivery is not available for this address.";

      setDeliveryError(errorMessage);
      setDeliveryPrice(0);

      // Mostrar modal de error
      Modal.error({
        title: "Delivery Not Available",
        content: `${errorMessage}. If you would like, you can select Pickup mode instead to continue with your order.`,
        okText: "Got it",
      });
    } finally {
      setIsCalculatingDelivery(false);
    }
  };

  // useEffect(() => {
  //   setTotal(total + deliveryPrice);
  // }, [deliveryPrice]);

  /* Calendar */

  const selectedMenuItem = (id) => {
    const selectedItem = menuItems.find(
      (menuItem) => String(menuItem.dish_id) === String(id),
    );

    if (!selectedItem) {
      return;
    }

    document
      .querySelectorAll(".single-menuItem-container_select")
      .forEach((element) => element.classList.remove("selected_service"));

    const selectedElement = document.getElementById(String(id));
    if (selectedElement) {
      selectedElement.classList.add("selected_service");
    }

    setSelectedMenuItem(selectedItem);
    setQuantity(1);
    setSelectedExtras([]);
    setModalOpen(true);
    setCurrentStep(0);
  };

  const handleExtrasChange = (checkedValues) => {
    setSelectedExtras(checkedValues);
  };

  const hideModal = (ref) => {
    setModalOpen(false);
    setQuantity(1);
    setSelectedExtras([]);
  };

  const onFinish = async (values) => {
    setIsSubmitting(true);

    try {
      // Validar que si es delivery, no haya error y el deliveryPrice ya esté calculado
      if (enable === 1 && deliveryError) {
        message.error(
          "Delivery is not available for this address. Please change your address or select Pickup mode.",
        );
        setIsSubmitting(false);
        return;
      }

      if (enable === 1 && deliveryPrice === 0) {
        message.error(
          "Please wait while we calculate the delivery fee for your address.",
        );
        setIsSubmitting(false);
        return;
      }

      setUserInfo(values);

      // Guardar en localStorage
      localStorage.setItem(
        `userInfo_restaurant_${business.id}`,
        JSON.stringify(values),
      );
      localStorage.setItem(
        `deliveryPrice_restaurant_${business.id}`,
        JSON.stringify(deliveryPrice),
      );
      localStorage.setItem(
        `deliveryType_restaurant_${business.id}`,
        JSON.stringify(enable),
      );

      message.success("Order confirmed!");

      // Cerrar el drawer de checkout y proceder directamente al checkout
      setCheckoutDrawerOpen(false);
      proceedToCheckout(
        values,
        deliveryPrice,
        enable === 1 ? "Delivery" : "Pickup",
      );
    } catch (err) {
      console.log(err.response);

      const modal = Modal.error({
        title: "Error Processing Order",
        content:
          err.response?.data?.message || "An error occurred. Please try again.",
      });

      setTimeout(() => {
        modal.destroy();
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addToCart = async () => {
    if (!canBuy) {
      message.error("Payment is not configured.");
      return;
    }

    const newCart = [...cart];
    let added = false;

    if (!SelectedMenuItem) {
      return;
    }

    const basePrice = getDishPrice(SelectedMenuItem);

    const formattedExtras = selectedExtras.map((extra) => ({
      id: extra.id,
      name: extra.name,
      quantity: 1,
    }));

    const extrasTotal = selectedExtras.reduce(
      (sum, extra) => sum + getNumberValue(extra.price),
      0,
    );

    for (let i = 0; i < newCart.length && !added; i++) {
      if (
        newCart[i].itemID === SelectedMenuItem.dish_id &&
        areExtrasEqual(newCart[i].extras, formattedExtras)
      ) {
        newCart[i].itemQuantity = newCart[i].itemQuantity + quantity;

        newCart[i].itemTotal =
          newCart[i].itemQuantity * (basePrice + extrasTotal);
        added = true;
      }
    }

    if (!added) {
      const cartItem = {
        itemID: SelectedMenuItem.dish_id,
        itemName: SelectedMenuItem.dish_name,
        itemPrice: basePrice,
        itemQuantity: quantity,
        businessID: SelectedMenuItem.restaurant_id,
        extras: formattedExtras,
        itemTotal: quantity * (basePrice + extrasTotal),
        businessStripeID: business.meta_data.stripe_account_id,
        itemType: "restaurant_product",
        itemDescription: SelectedMenuItem.dish_description,
        startTime: moment().format("YYYY-MM-DD hh:mm:ss"),
        dish_image: SelectedMenuItem.dish_image,
      };

      newCart.push(cartItem);
    }

    const nextTotal = getCartTotal(newCart);

    setCart(newCart);
    setSelectedExtras([]);
    setQuantity(1);
    setOpen(false);
    setModalOpen(false);
    setCurrentStep(0);
    setTotal(nextTotal);
    saveRestaurantCart(newCart, nextTotal);

    message.success("Item Added to Cart!");
  };

  const areExtrasEqual = (extras1, extras2) => {
    const firstExtras = Array.isArray(extras1) ? extras1 : [];
    const secondExtras = Array.isArray(extras2) ? extras2 : [];

    if (firstExtras.length !== secondExtras.length) return false;
    if (firstExtras.length === 0 && secondExtras.length === 0) return true;

    // Ordenar por id antes de comparar
    const sortedExtras1 = [...firstExtras].sort((a, b) => a.id - b.id);
    const sortedExtras2 = [...secondExtras].sort((a, b) => a.id - b.id);

    return sortedExtras1.every(
      (extra, index) => extra.id === sortedExtras2[index].id,
    );
  };

  const renderAddToCartButton = (onClick) => (
    <Tooltip title={!canBuy ? "Payment not configured" : ""} placement="top">
      <span>
        <Button type="primary" onClick={onClick} disabled={!canBuy}>
          Add to Cart
        </Button>
      </span>
    </Tooltip>
  );

  const handleCheckout = async () => {
    if (!canBuy) {
      message.error("Payment is not configured.");
      return;
    }

    // Validar que el carrito tenga items
    if (!cart || cart.length === 0) {
      message.error("Your cart is empty. Please add items before checkout.");
      return;
    }

    // Abrir el drawer de checkout para completar la información
    onClose(); // Cerrar el drawer del carrito
    setCheckoutDrawerOpen(true);
  };

  const proceedToCheckout = (
    checkoutUserInfo = userInfo,
    checkoutDeliveryPrice = deliveryPrice,
    checkoutDeliveryType = enable === 1 ? "Delivery" : "Pickup",
  ) => {
    if (!canBuy) {
      message.error("Payment is not configured.");
      return;
    }

    // Check to ensure that there are legitimate tickets being called
    if (cart && cart.length > 0) {
      // First, remove any item from localstorage
      localStorage.removeItem("cart");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("deliveryPrice");
      localStorage.removeItem("deliveryType");
      clearSavedRestaurantCart();

      // Then, add the new item in the local storage
      window.localStorage.setItem("cart", JSON.stringify(cart));
      window.localStorage.setItem("userInfo", JSON.stringify(checkoutUserInfo));
      window.localStorage.setItem(
        "deliveryPrice",
        JSON.stringify(checkoutDeliveryPrice),
      );
      window.localStorage.setItem(
        "deliveryType",
        JSON.stringify(checkoutDeliveryType),
      );

      localStorage.setItem("checkoutTip", JSON.stringify({
        cart: JSON.stringify(cart), amount: tipAmount,
      }));

      // redirect to checkout
      window.location.replace(`${FE_URL}/checkout`);
    } else {
      message.error("Your cart is empty. Please add items before checkout.");
    }
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const scrollToCategory = (category) => {
    const element = categoryRefs.current[category];
    if (element) {
      const offset = 180; // Altura del header sticky + margen
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveCategory(category);
    }
  };

  const menuItemsByCategory = useMemo(() => {
    return menuItems.reduce((groups, item) => {
      if (item.is_available === 1) {
        if (!groups[item.category]) {
          groups[item.category] = [];
        }
        groups[item.category].push(item);
      }

      return groups;
    }, {});
  }, [menuItems]);

  const offerItems = useMemo(
    () =>
      menuItems.filter(
        (item) => item.is_available === 1 && item.on_offer === 1,
      ),
    [menuItems],
  );

  const Menu = () => {
    return (
      <div>
        <div className="menuItem_container-menuItems">
          <RestaurantOffersCarousel
            offers={offerItems}
            onSelectOffer={selectedMenuItem}
          />
          {categories.length > 0 && (
            <div className="restaurant-inline-categories-menu">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`restaurant-inline-category-item ${
                    activeCategory === category ? "active" : ""
                  }`}
                  onClick={() => scrollToCategory(category)}
                >
                  {getCategoryDisplayName(category)}
                </button>
              ))}
            </div>
          )}
          {categories.map((category, index) => {
            const items = menuItemsByCategory[category] || [];

            return (
              <div
                key={category}
                className="menu-category"
                ref={(el) => (categoryRefs.current[category] = el)}
              >
                {index > 0 && <Divider />}
                <h3 className="menu-category-title">
                  {getCategoryDisplayName(category)}
                </h3>
                {items.map((menuItem, itemIndex) => (
                  <div
                    key={menuItem.dish_id + "_" + itemIndex}
                    className="single-menuItem-container"
                  >
                    <div
                      className="single-menuItem-container_select"
                      id={menuItem.dish_id}
                      onClick={(element) => {
                        selectedMenuItem(element.target.id);
                      }}
                    ></div>
                    <div className="single-menuItem-container_data">
                      <div className="single-menuItem-container_data-row-1">
                        <label>{menuItem.dish_name}</label>
                      </div>
                      <div className="single-menuItem-container_data-row-2">
                        <p>
                          {menuItem.dish_description ||
                            "Delicious dish prepared with quality ingredients."}
                        </p>
                      </div>
                      <div className="single-menuItem-container_data-row-3">
                        <div className="single-menuItem-container_data-row-3-price">
                          <label className="single-menuItem-container_data-row-3-price-label">
                            ${" "}
                            {menuItem.on_offer === 1
                              ? menuItem.dish_offer_price
                              : menuItem.dish_price}
                          </label>
                          {menuItem.on_offer === 1 && (
                            <>
                              <label className="single-menuItem-container_data-row-3-price-original">
                                ${menuItem.dish_price}
                              </label>
                              <label className="single-menuItem-container_data-row-3-price-discount">
                                {getDiscountPercentage(
                                  menuItem.dish_price,
                                  menuItem.dish_offer_price,
                                )}
                                % OFF
                              </label>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="single-menuItem-container_image">
                      {renderDishImage(menuItem)}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  //Change quantity from cart
  const changeHandler = (index, value) => {
    const currentCart = [...cart];
    const nextQuantity = value || 1;
    const currentQuantity = currentCart[index].itemQuantity || 1;
    const unitTotal =
      currentQuantity > 0
        ? currentCart[index].itemTotal / currentQuantity
        : currentCart[index].itemPrice;

    currentCart[index].itemQuantity = nextQuantity;
    currentCart[index].itemTotal = unitTotal * nextQuantity;

    const total_temp = getCartTotal(currentCart);

    setTotal(total_temp);
    setCart(currentCart);
    saveRestaurantCart(currentCart, total_temp);
  };

  //Delete from cart
  const removeHandler = (index) => {
    const currentCart = [...cart];
    currentCart.splice(index, 1);
    const nextTotal = getCartTotal(currentCart);

    setCart(currentCart);
    setTotal(nextTotal);
    saveRestaurantCart(currentCart, nextTotal);
  };

  return (
    <>
      {/* Sticky Header - aparece cuando el usuario hace scroll hacia abajo */}
      {showStickyHeader && (
        <div className="restaurant-sticky-header">
          <div className="restaurant-sticky-header-content">
            <div className="restaurant-sticky-header-left">
              <h2>{formatBusinessName(business.name)}</h2>
            </div>
          </div>
          {/* Menú de categorías horizontal */}
          {categories.length > 0 && (
            <div className="restaurant-categories-menu">
              {categories.map((category) => (
                <div
                  key={category}
                  className={`category-menu-item ${
                    activeCategory === category ? "active" : ""
                  }`}
                  onClick={() => scrollToCategory(category)}
                >
                  {getCategoryDisplayName(category)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Marcador invisible para detectar scroll */}
      <div ref={businessNameRef} className="restaurant-name-marker"></div>

      <FontAwesomeIcon icon={faCartShopping} onClick={showDrawer} />
      <span className="cart_quantity">{getCartItemCount(cart)}</span>
      <Drawer
        title={
          <div className="cart-drawer-title">
            <p>CART</p>
            <p className="cart-drawer-title-divider">-</p>
            <p className="cart-drawer-title-count">
              {getCartItemCount(cart)} Item
            </p>
          </div>
        }
        placement="right"
        onClose={onClose}
        visible={open}
        width="100%"
      >
        {cart.length > 0 &&
          cart.map((product, index) => {
            return (
              <div
                key={`${product.itemID}_${index}`}
                className="single-menuItem-container-cart"
              >
                <div className="single-menuItem-container-cart_image">
                  {renderDishImage(product)}
                </div>
                <div className="single-menuItem-container-cart_data">
                  <div className="single-menuItem-container_data-row-1">
                    <label>{product.itemName}</label>
                  </div>
                  <div className="single-menuItem-container_data-row-2-duration">
                    <label>Add: </label>{" "}
                    <label className="single-menuItem-container_data-row-2-duration-label">
                      {Array.isArray(product.extras) &&
                      product.extras.length > 0
                        ? product.extras.map((extra) => extra.name).join(", ")
                        : "None"}
                    </label>
                  </div>
                  <div className="single-menuItem-container_data-row-2-price">
                    <label>Price: </label>
                    <label className="single-menuItem-container_data-row-2-price-label">
                      $ {product.itemPrice}
                    </label>
                  </div>
                </div>
                <div className="single-menuItem-container-cart_quantity">
                  <InputNumber
                    min={1}
                    max={999}
                    value={product.itemQuantity}
                    onChange={(value) => changeHandler(index, value)}
                  />
                </div>
                <div
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you wish to delete this item from your cart?",
                      )
                    ) {
                      removeHandler(index);
                    }
                  }}
                  className="single-menuItem-container-cart_delete"
                >
                  <DeleteOutlined style={{ cursor: "pointer" }} />
                </div>
              </div>
            );
          })}
        {cart.length > 0 && (
          <>
            <Divider />
            <div style={{ width: "100%", height: "10%", display: "flex" }}>
              <div style={{ width: "50%", alignSelf: "center" }}>
                <p style={{ float: "left", margin: "0", fontSize: "18px" }}>
                  Order amount:
                </p>
              </div>
              <div style={{ width: "50%", alignSelf: "center" }}>
                <p
                  style={{
                    float: "right",
                    fontWeight: "bold",
                    margin: "0",
                    fontSize: "18px",
                  }}
                >
                  ${total.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="cart-drawer-actions">
              <Button size="large" block onClick={onClose}>
                Continue adding items
              </Button>
              <Tooltip
                title={!canBuy ? "Payment not configured" : ""}
                placement="top"
              >
                <span>
                  <Button
                    onClick={() => {
                      handleCheckout();
                    }}
                    type="primary"
                    size="large"
                    block
                    disabled={!canBuy}
                  >
                    Checkout
                  </Button>
                </span>
              </Tooltip>
            </div>
          </>
        )}
        {cart.length === 0 && (
          <p
            style={{
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            You have no items in your cart
          </p>
        )}
        <Button
          onClick={() => {
            window.localStorage.setItem("cart", JSON.stringify(cart));
          }}
        ></Button>
      </Drawer>

      <Modal
        centered
        visible={modalOpen}
        onCancel={hideModal}
        footer={null}
        width={1000}
        className="restaurant_modal"
      >
        {SelectedMenuItem != null && (
          <div className="modal_container">
            <div className="modal_image">
              {renderDishImage(SelectedMenuItem, undefined, "64px")}
            </div>
            <div className="modal_data">
              {currentStep === 0 ? (
                <div>
                  <h3>{SelectedMenuItem.dish_name}</h3>
                  {SelectedMenuItem.dish_description && (
                    <>
                      <Divider />
                      <h4>{SelectedMenuItem.dish_description}</h4>
                    </>
                  )}
                  <Divider />
                  <div className="modal_price_container">
                    <h5>
                      $
                      {SelectedMenuItem.on_offer === 1
                        ? SelectedMenuItem.dish_offer_price
                        : SelectedMenuItem.dish_price}
                    </h5>
                    {SelectedMenuItem.on_offer === 1 && (
                      <>
                        <h5 className="modal_price_original">
                          ${SelectedMenuItem.dish_price}
                        </h5>
                        <span className="modal_price_discount">
                          {getDiscountPercentage(
                            SelectedMenuItem.dish_price,
                            SelectedMenuItem.dish_offer_price,
                          )}
                          % OFF
                        </span>
                      </>
                    )}
                  </div>
                  <Divider />
                  <InputNumber
                    addonBefore="Quantity"
                    min={1}
                    max={10}
                    value={quantity}
                    onChange={(value) => setQuantity(value || 1)}
                  />
                  <Divider />
                  <div>
                    {getDishExtras(SelectedMenuItem).length > 0 ? (
                      <Button
                        type="primary"
                        onClick={() => {
                          setSelectedExtras([]);
                          setCurrentStep(1);
                        }}
                      >
                        Next
                      </Button>
                    ) : (
                      renderAddToCartButton(() => {
                        addToCart();
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <h3>Select Addons</h3>
                  <Divider />
                  <Checkbox.Group
                    options={getDishExtras(SelectedMenuItem).map((extra) => ({
                      label: `${extra.name} (+$${extra.price})`,
                      value: extra,
                    }))}
                    onChange={handleExtrasChange}
                  />
                  <Divider />
                  <div>
                    <Button onClick={() => setCurrentStep(0)}>Back</Button>
                    {renderAddToCartButton(addToCart)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <div className="menuItem_container">
        <Menu />
      </div>

      {/* Drawer para checkout con información de usuario */}
      <Drawer
        title="Complete Your Order"
        className="restaurant-checkout-drawer"
        placement="right"
        onClose={() => setCheckoutDrawerOpen(false)}
        visible={checkoutDrawerOpen}
        width="100%"
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          initialValues={userInfo}
          key={userInfo ? JSON.stringify(userInfo) : "empty"}
          onValuesChange={(changedValues, allValues) => {
            setFormValues(allValues);
          }}
        >
          <div className="">
            <h3>Select Delivery Type</h3>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                margin: "20px 0",
                padding: "15px",
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: enable === 0 ? "bold" : "normal",
                  color: enable === 0 ? "#1890ff" : "#666",
                }}
              >
                Pickup
              </span>
              <Switch
                checked={enable === 1}
                onChange={(checked) => setEnable(checked ? 1 : 0)}
                style={{
                  backgroundColor: enable === 1 ? "#1890ff" : "#00000040",
                }}
              />
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: enable === 1 ? "bold" : "normal",
                  color: enable === 1 ? "#1890ff" : "#666",
                }}
              >
                Delivery
              </span>
            </div>
            <Divider />
            {enable === 1 ? (
              <div>
                <h3>Delivery Information</h3>
                <Form.Item
                  name="phone_number"
                  label="Phone Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your phone number",
                    },
                  ]}
                >
                  <Input placeholder="Enter your phone number" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email (optional)"
                  normalize={(value) => value.trim()}
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                    {
                      max: 254,
                      message: "Email must be 254 characters or fewer",
                    },
                  ]}
                >
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email address"
                  />
                </Form.Item>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your address",
                    },
                  ]}
                >
                  <Input placeholder="Enter your address" />
                </Form.Item>
                <Form.Item
                  name="zip_code"
                  label="Zip Code"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your zip code",
                    },
                  ]}
                >
                  <Input placeholder="Enter your zip code" />
                </Form.Item>

                {/* Botón para calcular delivery fee */}
                <Button
                  type="primary"
                  onClick={calculateDeliveryPrice}
                  loading={isCalculatingDelivery}
                  block
                  size="large"
                  style={{
                    marginTop: "15px",
                    marginBottom: "10px",
                    height: "45px",
                    fontWeight: "500",
                    fontSize: "15px",
                  }}
                >
                  {deliveryPrice > 0 && !deliveryError
                    ? `Recalculate Delivery Fee ($${deliveryPrice.toFixed(2)})`
                    : "Calculate Delivery Fee"}
                </Button>
              </div>
            ) : (
              <div>
                <h3>Pickup Information</h3>
                <Form.Item
                  name="phone_number"
                  label="Phone Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your phone number",
                    },
                  ]}
                >
                  <Input placeholder="Enter your phone number" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email (optional)"
                  normalize={(value) => value.trim()}
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                    {
                      max: 254,
                      message: "Email must be 254 characters or fewer",
                    },
                  ]}
                >
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email address"
                  />
                </Form.Item>
              </div>
            )}

            <Divider />

            {/* Order Summary */}
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Order Summary</h3>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span>Order amount:</span>
                <span style={{ fontWeight: "500" }}>${total.toFixed(2)}</span>
              </div>
              {enable === 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <span>Delivery Price:</span>
                  <span style={{ fontWeight: "500" }}>
                    {isCalculatingDelivery ? (
                      <span style={{ color: "#1890ff" }}>Calculating...</span>
                    ) : deliveryError ? (
                      <span style={{ color: "#ff4d4f", fontSize: "12px" }}>
                        Not available
                      </span>
                    ) : deliveryPrice > 0 ? (
                      `$${deliveryPrice.toFixed(2)}`
                    ) : (
                      <span style={{ color: "#999", fontSize: "12px" }}>
                        Click "Calculate" button
                      </span>
                    )}
                  </span>
                </div>
              )}
              <Divider style={{ margin: "10px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "18px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Subtotal:</span>
                <span style={{ fontWeight: "bold", color: "#1890ff" }}>
                  $
                  {enable === 1
                    ? (total + deliveryPrice).toFixed(2)
                    : total.toFixed(2)}
                </span>
              </div>
            </div>

            <TipSelector selection={tipSelection} onSelectionChange={setTipSelection}
              customAmount={customTip} onCustomChange={setCustomTip} amount={tipAmount} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong>Amount including tip (before taxes and fees)</strong>
              <strong>${(total + (enable === 1 ? deliveryPrice : 0) + tipAmount).toFixed(2)}</strong>
            </div>

            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Form.Item
                style={{
                  marginBottom: 0,
                }}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  size="large"
                >
                  Complete Order
                </Button>
              </Form.Item>
            </div>
          </div>
        </Form>
      </Drawer>
    </>
  );
}
